import { FridgeTempsTab } from './FridgeTemps';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  ArrowLeftRight, ClipboardList, CheckSquare, FileText,
  Thermometer, ChevronDown, ChevronUp,
  X, User, Sparkles
} from 'lucide-react';
import { isAdminUser, getUserVenues } from '../userUtils';

// ─── Colour system ────────────────────────────────────────────────────────────
const C = {
  yellow:'#F7EC71', yellowLight:'#FEFCE8',
  orange:'#E4AC49', orangeLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF',
  cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3',
  skyBlue:'#BCD9E5', skyBlueLight:'#EAF4F8', skyText:'#3A8FA3',
  white:'#FFFFFF', red:'#DC3545', redLight:'#FFF0F0',
  amber:'#E07B00', amberLight:'#FFF3E0',
  pink:'#F2B8CB', pinkLight:'#FDF0F5', pinkActive:'#C4607A',
};

const VENUE_COLORS  = { 'Box Jordaan': C.orange, 'Box Plantage': C.skyBlue, 'Sassys': C.green };
const LINE_COLORS   = [C.orange, C.skyText, C.green, C.pinkActive, C.navy, C.amber, C.red];
const VENUES        = ['Box Jordaan', 'Box Plantage', 'Sassys'];
const ALL_VENUES    = ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen'];
const DEPTS         = ['kitchen', 'bar'];
const NO_BAR_VENUES = ['Prep Kitchen', 'Sassys'];
const DAYS_OF_WEEK  = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ── shifts that have bar/floor sub-splits ─────────────────────────────────────
const SUB_SHIFT_MAP = {
  'mid-shift': { bar: 'mid-shift-bar', floor: 'mid-shift-floor' },
  'closing':   { bar: 'closing-bar',   floor: 'closing-floor'   },
};

// ─── Tab config ───────────────────────────────────────────────────────────────
const TABS = [
  { key: 'transfers',   label: 'Transfers',    icon: ArrowLeftRight, adminOnly: true  },
  { key: 'checklists',  label: 'Checklists',   icon: CheckSquare,    adminOnly: false },
  { key: 'cleaning',    label: 'Cleaning',     icon: Sparkles,       adminOnly: false },
  { key: 'shifty',      label: 'Shifty',       icon: FileText,       adminOnly: false },
  { key: 'fridgetemps', label: 'Fridge Temps', icon: Thermometer,    adminOnly: false },
  { key: 'stocktake',   label: 'Stocktake',    icon: ClipboardList,  adminOnly: false },
];

// ─── Date helpers ─────────────────────────────────────────────────────────────
function getMonthOptions() {
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return { label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }), year: d.getFullYear(), month: d.getMonth() };
  });
}
function getWeekOptions() {
  const weeks = [];
  const today = new Date(); today.setHours(0,0,0,0);
  const dow   = today.getDay();
  const daysToMonday = dow === 0 ? 6 : dow - 1;
  for (let i = 0; i < 8; i++) {
    const monday = new Date(today); monday.setDate(today.getDate() - daysToMonday - i * 7);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    weeks.push({ label: `${monday.toLocaleDateString('en-GB',{day:'numeric',month:'short'})} – ${sunday.toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}`, start: monday, end: sunday });
  }
  return weeks;
}
function getWeekDates(start) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
}
function isPast(dateStr) {
  const [y,m,d] = dateStr.split('-').map(Number);
  const today = new Date(); today.setHours(0,0,0,0);
  return new Date(y, m-1, d) < today;
}
function getDayName(dateStr) {
  const [y,m,d] = dateStr.split('-').map(Number);
  const dow = new Date(y, m-1, d).getDay();
  return DAYS_OF_WEEK[dow === 0 ? 6 : dow - 1];
}
function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
}
function formatTime(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' });
}
function formatDateTime(isoStr) {
  if (!isoStr) return '';
  return `${formatDate(isoStr)} · ${formatTime(isoStr)}`;
}

// ─── Shared UI ────────────────────────────────────────────────────────────────
const MONTH_OPTIONS = getMonthOptions();
const WEEK_OPTIONS  = getWeekOptions();

function Select({ value, onChange, options, style }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ padding:'0.32rem 0.6rem', border:`1px solid ${C.border}`, borderRadius:'7px', fontSize:'0.8rem', color:C.charcoal, background:C.white, cursor:'pointer', ...style }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function SectionHeader({ title, children }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.65rem', marginBottom:'1.25rem' }}>
      <h3 style={{ margin:0, fontSize:'1rem', fontWeight:700, color:C.charcoal }}>{title}</h3>
      <div style={{ display:'flex', gap:'0.45rem', flexWrap:'wrap', alignItems:'center' }}>{children}</div>
    </div>
  );
}

function Card({ children, style }) {
  return <div style={{ background:C.white, borderRadius:'14px', padding:'1.25rem', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', marginBottom:'1.25rem', ...style }}>{children}</div>;
}

function rateColor(rate) {
  if (rate === null) return C.mid;
  if (rate === 100)  return C.green;
  if (rate >= 70)    return C.amber;
  return C.red;
}

function WeekTh({ dates }) {
  return dates.map(date => {
    const [y,m,d] = date.split('-').map(Number);
    const dow = new Date(y,m-1,d).getDay();
    const day = DAYS_OF_WEEK[dow === 0 ? 6 : dow - 1];
    return (
      <th key={date} style={{ padding:'0.4rem', textAlign:'center', fontSize:'0.7rem', color:C.mid, fontWeight:600, minWidth:'46px' }}>
        <div>{day}</div><div style={{ fontWeight:400, color:C.border }}>{d}</div>
      </th>
    );
  });
}

const thStyle = { padding:'0.45rem 0.75rem', textAlign:'left', fontSize:'0.7rem', color:C.mid, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', borderBottom:`1px solid ${C.border}` };
const tdStyle = { padding:'0.5rem 0.75rem', fontSize:'0.85rem', color:C.charcoal, borderBottom:`1px solid ${C.border}88` };

function TableLegend() {
  return (
    <div style={{ marginTop:'0.85rem', display:'flex', gap:'1.1rem', fontSize:'0.75rem', color:C.mid, flexWrap:'wrap' }}>
      <span><span style={{ color:C.green, fontWeight:700 }}>✓</span> Done</span>
      <span><span style={{ color:C.amber, fontWeight:700 }}>⚠</span> Incomplete</span>
      <span><span style={{ color:C.red }}>●</span> Missed</span>
      <span style={{ color:C.border }}>— Not yet due</span>
    </div>
  );
}

// ─── Standard checklist audit drawer ─────────────────────────────────────────
function ChecklistAuditDrawer({ entry, venue, shift, date, onClose }) {
  const shiftLabel = s => s === 'mid-shift' ? 'Mid-Shift' : s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

  const items       = entry?.items || [];
  const doneItems   = items.filter(i => i.initials || i.checked);
  const missedItems = items.filter(i => !i.initials && !i.checked);

  const statusConfig = {
    complete: { label: 'Complete',      color: C.green, bg: C.greenLight },
    partial:  { label: 'Partial',       color: C.amber, bg: C.amberLight },
    missed:   { label: 'Not Submitted', color: C.red,   bg: C.redLight   },
  };

  const status = entry ? (entry.status || 'partial') : 'missed';
  const sc = statusConfig[status] || statusConfig.missed;

  function handleBackdropClick(e) { if (e.target === e.currentTarget) onClose(); }

  useEffect(() => {
    function onKeyDown(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div onClick={handleBackdropClick} style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(47,58,66,0.45)', display:'flex', alignItems:'center', justifyContent:'center', padding:'1.25rem', backdropFilter:'blur(2px)' }}>
      <div style={{ background:C.white, borderRadius:'18px', width:'100%', maxWidth:'680px', maxHeight:'85vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.22)', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'1.1rem 1.25rem 0.9rem', borderBottom:`1px solid ${C.border}`, position:'sticky', top:0, background:C.white, zIndex:1, borderRadius:'18px 18px 0 0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:'0.55rem', flexWrap:'wrap', marginBottom:'0.3rem' }}>
                <span style={{ fontSize:'0.7rem', fontWeight:700, padding:'0.18rem 0.55rem', borderRadius:'999px', background:sc.bg, color:sc.color, textTransform:'uppercase', letterSpacing:'0.04em' }}>{sc.label}</span>
                <span style={{ fontSize:'0.72rem', color:C.mid, background:C.cream, padding:'0.15rem 0.5rem', borderRadius:'999px' }}>{venue}</span>
                <span style={{ fontSize:'0.72rem', color:C.mid, background:C.cream, padding:'0.15rem 0.5rem', borderRadius:'999px' }}>{shiftLabel(shift)}</span>
              </div>
              <h3 style={{ margin:'0 0 0.15rem', fontSize:'1.05rem', fontWeight:700, color:C.charcoal }}>{formatDate(date)}</h3>
              {entry && (
                <p style={{ margin:0, fontSize:'0.75rem', color:C.mid }}>
                  {entry.finalised_by ? `Submitted by ${entry.finalised_by}` : 'Submitter unknown'}
                  {entry.submitted_at ? ` · ${formatDateTime(entry.submitted_at)}` : ''}
                </p>
              )}
            </div>
            <button onClick={onClose} style={{ background:C.cream, border:'none', borderRadius:'8px', padding:'0.4rem', cursor:'pointer', display:'flex', alignItems:'center', color:C.mid, flexShrink:0 }}>
              <X size={16} />
            </button>
          </div>
        </div>

        <div style={{ padding:'1.1rem 1.25rem 1.5rem', flex:1 }}>
          {!entry ? (
            <div style={{ textAlign:'center', padding:'2.5rem 0', color:C.mid }}>
              <div style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>●</div>
              <p style={{ margin:0, fontWeight:600 }}>No checklist submitted</p>
              <p style={{ margin:'0.3rem 0 0', fontSize:'0.82rem' }}>This shift was not recorded.</p>
            </div>
          ) : (
            <>
              <div style={{ display:'flex', gap:'0.65rem', marginBottom:'1.1rem', flexWrap:'wrap' }}>
                <div style={{ flex:'1 1 120px', padding:'0.7rem 0.9rem', background:C.greenLight, borderRadius:'10px', textAlign:'center' }}>
                  <p style={{ margin:'0 0 0.1rem', fontSize:'1.4rem', fontWeight:800, color:C.green }}>{doneItems.length}</p>
                  <p style={{ margin:0, fontSize:'0.72rem', color:C.green }}>Completed</p>
                </div>
                <div style={{ flex:'1 1 120px', padding:'0.7rem 0.9rem', background:missedItems.length > 0 ? C.redLight : C.cream, borderRadius:'10px', textAlign:'center' }}>
                  <p style={{ margin:'0 0 0.1rem', fontSize:'1.4rem', fontWeight:800, color:missedItems.length > 0 ? C.red : C.mid }}>{missedItems.length}</p>
                  <p style={{ margin:0, fontSize:'0.72rem', color:missedItems.length > 0 ? C.red : C.mid }}>Missed</p>
                </div>
                <div style={{ flex:'1 1 120px', padding:'0.7rem 0.9rem', background:C.cream, borderRadius:'10px', textAlign:'center' }}>
                  <p style={{ margin:'0 0 0.1rem', fontSize:'1.4rem', fontWeight:800, color:C.charcoal }}>{items.length}</p>
                  <p style={{ margin:0, fontSize:'0.72rem', color:C.mid }}>Total Items</p>
                </div>
              </div>
              {items.length > 0 && (
                <div style={{ marginBottom:'1rem' }}>
                  <p style={{ margin:'0 0 0.65rem', fontSize:'0.72rem', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.04em' }}>Checklist Items</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem' }}>
                    {items.map((item, idx) => {
                      const done = !!(item.initials || item.checked);
                      return (
                        <div key={item.id || idx} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.55rem 0.8rem', background:done ? C.greenLight : C.redLight, borderRadius:'8px', border:`1px solid ${done ? C.green+'44' : C.red+'33'}` }}>
                          <span style={{ fontSize:'0.9rem', flexShrink:0 }}>{done ? '✓' : '○'}</span>
                          <span style={{ flex:1, fontSize:'0.85rem', color:C.charcoal, fontWeight:done ? 400 : 500 }}>{item.label}</span>
                          {done && item.initials && (
                            <span style={{ display:'flex', alignItems:'center', gap:'0.3rem', fontSize:'0.72rem', color:C.mid, flexShrink:0 }}>
                              <User size={11} />
                              <strong style={{ color:C.charcoal }}>{item.initials}</strong>
                              {item.initialled_at && <span style={{ color:C.border }}>· {formatTime(item.initialled_at)}</span>}
                            </span>
                          )}
                          {!done && <span style={{ fontSize:'0.7rem', color:C.red, fontWeight:600, flexShrink:0 }}>Not done</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {entry.notes && (
                <div style={{ padding:'0.75rem 0.9rem', background:C.cream, borderRadius:'10px', border:`1px solid ${C.border}` }}>
                  <p style={{ margin:'0 0 0.25rem', fontSize:'0.7rem', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.04em' }}>Notes / Handover</p>
                  <p style={{ margin:0, fontSize:'0.85rem', color:C.charcoal, lineHeight:1.6 }}>{entry.notes}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── CHANGE: combined mid-shift audit drawer with Bar / Floor tabs ──────────────
function MidShiftAuditDrawer({ venue, date, shiftLabel = 'Mid-Shift', barEntry, floorEntry, onClose }) {
  const [activeTab, setActiveTab] = useState('bar');

  useEffect(() => {
    function onKeyDown(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  function handleBackdropClick(e) { if (e.target === e.currentTarget) onClose(); }

  const entry = activeTab === 'bar' ? barEntry : floorEntry;

  const statusConfig = {
    complete: { label: 'Complete',      color: C.green, bg: C.greenLight },
    partial:  { label: 'Partial',       color: C.amber, bg: C.amberLight },
    missed:   { label: 'Not Submitted', color: C.red,   bg: C.redLight   },
  };

  const status = entry ? (entry.status || 'partial') : 'missed';
  const sc     = statusConfig[status] || statusConfig.missed;
  const items       = entry?.items || [];
  const doneItems   = items.filter(i => i.initials || i.checked);
  const missedItems = items.filter(i => !i.initials && !i.checked);

  return (
    <div onClick={handleBackdropClick} style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(47,58,66,0.45)', display:'flex', alignItems:'center', justifyContent:'center', padding:'1.25rem', backdropFilter:'blur(2px)' }}>
      <div style={{ background:C.white, borderRadius:'18px', width:'100%', maxWidth:'680px', maxHeight:'85vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.22)', display:'flex', flexDirection:'column' }}>

        {/* Sticky header */}
        <div style={{ padding:'1.1rem 1.25rem 0.9rem', borderBottom:`1px solid ${C.border}`, position:'sticky', top:0, background:C.white, zIndex:1, borderRadius:'18px 18px 0 0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:'0.55rem', flexWrap:'wrap', marginBottom:'0.3rem' }}>
                <span style={{ fontSize:'0.72rem', color:C.mid, background:C.cream, padding:'0.15rem 0.5rem', borderRadius:'999px' }}>{venue}</span>
                <span style={{ fontSize:'0.72rem', color:C.skyText, background:C.skyBlueLight, padding:'0.15rem 0.5rem', borderRadius:'999px', fontWeight:600 }}>{shiftLabel}</span>
              </div>
              <h3 style={{ margin:'0 0 0.15rem', fontSize:'1.05rem', fontWeight:700, color:C.charcoal }}>{formatDate(date)}</h3>
            </div>
            <button onClick={onClose} style={{ background:C.cream, border:'none', borderRadius:'8px', padding:'0.4rem', cursor:'pointer', display:'flex', alignItems:'center', color:C.mid, flexShrink:0 }}>
              <X size={16} />
            </button>
          </div>

          {/* Bar / Floor tabs */}
          <div style={{ display:'flex', gap:'0.35rem', marginTop:'0.75rem' }}>
            {[
              { key:'bar',   label:'Bar',   e: barEntry   },
              { key:'floor', label:'Floor', e: floorEntry },
            ].map(({ key, label, e }) => {
              const isActive  = activeTab === key;
              const tabStatus = e ? (e.status === 'complete' ? 'complete' : 'partial') : 'missed';
              const tabColor  = tabStatus === 'complete' ? C.green : tabStatus === 'partial' ? C.amber : C.red;
              return (
                <button key={key} onClick={() => setActiveTab(key)} style={{ padding:'0.38rem 1rem', borderRadius:'8px', border:`2px solid ${isActive ? C.skyText : C.border}`, background:isActive ? C.skyBlueLight : C.white, color:isActive ? C.skyText : C.mid, fontSize:'0.82rem', fontWeight:isActive ? 700 : 400, cursor:'pointer', display:'flex', alignItems:'center', gap:'0.4rem' }}>
                  {label}
                  <span style={{ fontSize:'0.68rem', fontWeight:700, color:tabColor }}>
                    {tabStatus === 'complete' ? '✓' : tabStatus === 'partial' ? '⚠' : '●'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding:'1.1rem 1.25rem 1.5rem', flex:1 }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', padding:'0.25rem 0.75rem', background:sc.bg, borderRadius:'999px', marginBottom:'1rem' }}>
            <span style={{ fontSize:'0.72rem', fontWeight:700, color:sc.color, textTransform:'uppercase', letterSpacing:'0.04em' }}>{sc.label}</span>
          </div>

          {!entry ? (
            <div style={{ textAlign:'center', padding:'2.5rem 0', color:C.mid }}>
              <div style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>●</div>
              <p style={{ margin:0, fontWeight:600 }}>No checklist submitted</p>
              <p style={{ margin:'0.3rem 0 0', fontSize:'0.82rem' }}>This sub-shift was not recorded.</p>
            </div>
          ) : (
            <>
              <div style={{ display:'flex', gap:'0.65rem', marginBottom:'1.1rem', flexWrap:'wrap' }}>
                <div style={{ flex:'1 1 120px', padding:'0.7rem 0.9rem', background:C.greenLight, borderRadius:'10px', textAlign:'center' }}>
                  <p style={{ margin:'0 0 0.1rem', fontSize:'1.4rem', fontWeight:800, color:C.green }}>{doneItems.length}</p>
                  <p style={{ margin:0, fontSize:'0.72rem', color:C.green }}>Completed</p>
                </div>
                <div style={{ flex:'1 1 120px', padding:'0.7rem 0.9rem', background:missedItems.length > 0 ? C.redLight : C.cream, borderRadius:'10px', textAlign:'center' }}>
                  <p style={{ margin:'0 0 0.1rem', fontSize:'1.4rem', fontWeight:800, color:missedItems.length > 0 ? C.red : C.mid }}>{missedItems.length}</p>
                  <p style={{ margin:0, fontSize:'0.72rem', color:missedItems.length > 0 ? C.red : C.mid }}>Missed</p>
                </div>
                <div style={{ flex:'1 1 120px', padding:'0.7rem 0.9rem', background:C.cream, borderRadius:'10px', textAlign:'center' }}>
                  <p style={{ margin:'0 0 0.1rem', fontSize:'1.4rem', fontWeight:800, color:C.charcoal }}>{items.length}</p>
                  <p style={{ margin:0, fontSize:'0.72rem', color:C.mid }}>Total Items</p>
                </div>
              </div>

              {items.length > 0 && (
                <div style={{ marginBottom:'1rem' }}>
                  <p style={{ margin:'0 0 0.65rem', fontSize:'0.72rem', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.04em' }}>Checklist Items</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem' }}>
                    {items.map((item, idx) => {
                      const done = !!(item.initials || item.checked);
                      return (
                        <div key={item.id || idx} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.55rem 0.8rem', background:done ? C.greenLight : C.redLight, borderRadius:'8px', border:`1px solid ${done ? C.green+'44' : C.red+'33'}` }}>
                          <span style={{ fontSize:'0.9rem', flexShrink:0 }}>{done ? '✓' : '○'}</span>
                          <span style={{ flex:1, fontSize:'0.85rem', color:C.charcoal, fontWeight:done ? 400 : 500 }}>{item.label}</span>
                          {done && item.initials && (
                            <span style={{ display:'flex', alignItems:'center', gap:'0.3rem', fontSize:'0.72rem', color:C.mid, flexShrink:0 }}>
                              <User size={11} />
                              <strong style={{ color:C.charcoal }}>{item.initials}</strong>
                              {item.initialled_at && <span style={{ color:C.border }}>· {formatTime(item.initialled_at)}</span>}
                            </span>
                          )}
                          {!done && <span style={{ fontSize:'0.7rem', color:C.red, fontWeight:600, flexShrink:0 }}>Not done</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {entry.notes && (
                <div style={{ padding:'0.75rem 0.9rem', background:C.cream, borderRadius:'10px', border:`1px solid ${C.border}` }}>
                  <p style={{ margin:'0 0 0.25rem', fontSize:'0.7rem', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.04em' }}>Notes / Handover</p>
                  <p style={{ margin:0, fontSize:'0.85rem', color:C.charcoal, lineHeight:1.6 }}>{entry.notes}</p>
                </div>
              )}

              {entry.finalised_by && (
                <p style={{ margin:'0.75rem 0 0', fontSize:'0.75rem', color:C.mid }}>
                  Submitted by {entry.finalised_by}{entry.submitted_at ? ` · ${formatDateTime(entry.submitted_at)}` : ''}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── TRANSFERS TAB ────────────────────────────────────────────────────────────
function MonthStepper({ selectedMonth, onChange }) {
  const idx     = MONTH_OPTIONS.findIndex(o => o.year === selectedMonth.year && o.month === selectedMonth.month);
  const canPrev = idx < MONTH_OPTIONS.length - 1;
  const canNext = idx > 0;
  const btnStyle = (enabled) => ({
    background: 'none', border: `1px solid ${C.border}`, borderRadius: '7px',
    padding: '0.28rem 0.55rem', cursor: enabled ? 'pointer' : 'not-allowed',
    display: 'flex', alignItems: 'center', color: enabled ? C.charcoal : C.border,
    transition: 'all 0.12s',
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
      <button style={btnStyle(canPrev)} disabled={!canPrev} onClick={() => canPrev && onChange(MONTH_OPTIONS[idx + 1])}>
        <ChevronDown size={14} style={{ transform: 'rotate(90deg)' }} />
      </button>
      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: C.charcoal, minWidth: '110px', textAlign: 'center' }}>
        {selectedMonth.label}
      </span>
      <button style={btnStyle(canNext)} disabled={!canNext} onClick={() => canNext && onChange(MONTH_OPTIONS[idx - 1])}>
        <ChevronDown size={14} style={{ transform: 'rotate(-90deg)' }} />
      </button>
    </div>
  );
}

function TransfersTab({ transferLog }) {
  const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[0]);
  const [venueFilter,   setVenueFilter]   = useState('All');
  const [requests,      setRequests]      = useState([]);
  const [loading,       setLoading]       = useState(false);

  useEffect(() => {
    setLoading(true);
    const start = new Date(selectedMonth.year, selectedMonth.month, 1).toISOString().split('T')[0];
    const end   = new Date(selectedMonth.year, selectedMonth.month + 1, 0).toISOString().split('T')[0];
    supabase.from('requests').select('id, venue, order_from, date, status, items')
      .in('venue', VENUES).not('order_from', 'is', null).eq('status', 'received')
      .gte('date', start).lte('date', end)
      .then(({ data, error }) => { if (!error) setRequests(data || []); setLoading(false); });
  }, [selectedMonth]);

  function itemValue(item) {
    const qty   = item.quantityReceived ?? item.quantity ?? 0;
    const price = item.price ?? 0;
    return qty * price;
  }
  function getDept(item) { return (item.dept || 'kitchen').toLowerCase(); }

  const netByVenue = VENUES.map(venue => {
    const venueReqs = requests.filter(r => r.venue === venue);
    let bar = 0, kitchen = 0;
    venueReqs.forEach(r => {
      (r.items || []).forEach(item => {
        const dept = getDept(item);
        const val  = itemValue(item);
        if (dept === 'bar') bar += val; else kitchen += val;
      });
    });
    return { venue, bar: Math.round(bar*100)/100, kitchen: Math.round(kitchen*100)/100, total: Math.round((bar+kitchen)*100)/100 };
  });

  const [monthlyData,   setMonthlyData]   = useState([]);
  const [chartLoading,  setChartLoading]  = useState(false);

  useEffect(() => {
    setChartLoading(true);
    const oldest = MONTH_OPTIONS[MONTH_OPTIONS.length - 1];
    const start  = new Date(oldest.year, oldest.month, 1).toISOString().split('T')[0];
    const end    = new Date(MONTH_OPTIONS[0].year, MONTH_OPTIONS[0].month + 1, 0).toISOString().split('T')[0];
    supabase.from('requests').select('venue, date, items').in('venue', VENUES).not('order_from','is',null).eq('status','received').gte('date',start).lte('date',end)
      .then(({ data, error }) => {
        if (error) { setChartLoading(false); return; }
        const rows = data || [];
        const buckets = {};
        MONTH_OPTIONS.slice().reverse().forEach(m => {
          buckets[`${m.year}-${m.month}`] = { month: m.label.split(' ')[0], 'Box Jordaan':0, 'Box Plantage':0, 'Sassys':0 };
        });
        rows.forEach(r => {
          const d   = new Date(r.date);
          const key = `${d.getFullYear()}-${d.getMonth()}`;
          if (!buckets[key]) return;
          const total = (r.items||[]).reduce((sum, item) => sum + (item.quantityReceived??item.quantity??0)*(item.price??0), 0);
          buckets[key][r.venue] = Math.round((buckets[key][r.venue] + total)*100)/100;
        });
        setMonthlyData(Object.values(buckets));
        setChartLoading(false);
      });
  }, []);

  const filteredChart = venueFilter === 'All' ? monthlyData : monthlyData.map(r => ({ month: r.month, [venueFilter]: r[venueFilter] }));
  const visibleVenues = venueFilter === 'All' ? VENUES : [venueFilter];
  const [showDetail, setShowDetail] = useState(false);
  const detailRows = requests.slice().sort((a,b) => b.date.localeCompare(a.date));

  return (
    <>
      <Card>
        <SectionHeader title="Net Transfer Value per Venue">
          <MonthStepper selectedMonth={selectedMonth} onChange={setSelectedMonth} />
        </SectionHeader>
        {loading ? <p style={{color:C.mid,fontSize:'0.85rem'}}>Loading…</p> : (
          <>
            {netByVenue.map(({ venue, bar, kitchen, total }) => {
              const maxAbs = Math.max(...netByVenue.map(v=>Math.abs(v.total)),1);
              return (
                <div key={venue} style={{ marginBottom:'1rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.28rem' }}>
                    <span style={{ fontSize:'0.9rem', fontWeight:500, color:C.charcoal }}>{venue}</span>
                    <span style={{ fontSize:'0.9rem', fontWeight:700, color:total>0?C.charcoal:total<0?C.red:C.mid }}>{total>0?'+':''}€{total.toFixed(2)}</span>
                  </div>
                  <div style={{ height:'9px', background:C.cream, borderRadius:'999px', overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${Math.abs(total)/maxAbs*100}%`, background:total>=0?(VENUE_COLORS[venue]||C.navy):C.red, borderRadius:'999px', transition:'width 0.3s', minWidth:total!==0?'4px':'0' }} />
                  </div>
                  <div style={{ display:'flex', gap:'1rem', marginTop:'0.25rem' }}>
                    <span style={{ fontSize:'0.73rem', color:C.mid }}>Bar: <strong>€{bar.toFixed(2)}</strong></span>
                    <span style={{ fontSize:'0.73rem', color:C.mid }}>Kitchen: <strong>€{kitchen.toFixed(2)}</strong></span>
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop:'0.75rem', paddingTop:'0.75rem', borderTop:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontSize:'0.82rem', color:C.mid, fontWeight:600 }}>Total received this month</span>
              <span style={{ fontSize:'0.9rem', fontWeight:800, color:C.charcoal }}>€{netByVenue.reduce((s,v)=>s+v.total,0).toFixed(2)}</span>
            </div>
            <button onClick={()=>setShowDetail(d=>!d)} style={{ marginTop:'0.85rem', background:'none', border:`1px solid ${C.border}`, borderRadius:'8px', padding:'0.3rem 0.75rem', fontSize:'0.78rem', color:C.mid, cursor:'pointer', display:'flex', alignItems:'center', gap:'0.35rem' }}>
              {showDetail ? <ChevronUp size={13}/> : <ChevronDown size={13}/>} {showDetail?'Hide':'Show'} individual transfers
            </button>
            {showDetail && detailRows.length > 0 && (
              <div style={{ overflowX:'auto', marginTop:'0.85rem' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                  <thead><tr>{['Date','To','From','Dept','Items','Value'].map(h=><th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                  <tbody>
                    {detailRows.map(r => {
                      const depts = [...new Set((r.items||[]).map(i=>getDept(i)))];
                      const value = (r.items||[]).reduce((s,i)=>s+itemValue(i),0);
                      return (
                        <tr key={r.id}>
                          <td style={tdStyle}>{formatDate(r.date)}</td>
                          <td style={tdStyle}>{r.venue}</td>
                          <td style={tdStyle}>{r.order_from}</td>
                          <td style={tdStyle}>{depts.map(d=>d.charAt(0).toUpperCase()+d.slice(1)).join(', ')}</td>
                          <td style={tdStyle}>{(r.items||[]).length}</td>
                          <td style={{...tdStyle,fontWeight:700}}>€{value.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {showDetail && detailRows.length === 0 && <p style={{color:C.mid,fontSize:'0.82rem',marginTop:'0.75rem'}}>No received transfers for this period.</p>}
          </>
        )}
      </Card>
      <Card>
        <SectionHeader title="Monthly Net Transfer Value">
          <Select value={venueFilter} onChange={setVenueFilter} options={[{value:'All',label:'All Venues'},...VENUES.map(v=>({value:v,label:v}))]} />
        </SectionHeader>
        {chartLoading ? <p style={{color:C.mid,fontSize:'0.85rem'}}>Loading…</p> : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={filteredChart} margin={{top:4,right:8,left:0,bottom:4}}>
              <XAxis dataKey="month" tick={{fontSize:11}} />
              <YAxis tick={{fontSize:11}} tickFormatter={v=>`€${v}`} />
              <Tooltip formatter={v=>`€${v.toFixed(2)}`} />
              <Legend />
              {visibleVenues.map(v => <Bar key={v} dataKey={v} stackId="a" fill={VENUE_COLORS[v]||C.navy} radius={[3,3,0,0]} />)}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </>
  );
}

// ─── STOCKTAKE TAB ────────────────────────────────────────────────────────────
function StocktakeTab({ allowedVenues }) {
  const [selectedWeek,   setSelectedWeek]   = useState(WEEK_OPTIONS[0]);
  const [venueFilter,    setVenueFilter]    = useState('All');
  const [deptFilter,     setDeptFilter]     = useState('All');
  const [stocktakeLogs,  setStocktakeLogs]  = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [stockMovements, setStockMovements] = useState([]);
  const [stockProduct,   setStockProduct]   = useState('All');
  const [stockMonth,     setStockMonth]     = useState(MONTH_OPTIONS[0]);
  const [stockLoading,   setStockLoading]   = useState(false);

  const venuesForTab = allowedVenues ? ALL_VENUES.filter(v=>allowedVenues.includes(v)) : ALL_VENUES;

  useEffect(() => {
    setLoading(true);
    const start = selectedWeek.start.toISOString().split('T')[0];
    const end   = selectedWeek.end.toISOString().split('T')[0];
    supabase.from('stocktake_log').select('id,venue,dept,date,submitted_by,status').gte('date',start).lte('date',end)
      .then(({ data }) => { setStocktakeLogs(data||[]); setLoading(false); });
  }, [selectedWeek]);

  useEffect(() => {
    setStockLoading(true);
    const start = new Date(stockMonth.year, stockMonth.month, 1).toISOString().split('T')[0];
    const end   = new Date(stockMonth.year, stockMonth.month+1, 0).toISOString().split('T')[0];
    supabase.from('stock_movements').select('*').eq('venue','Prep Kitchen').gte('date',start).lte('date',end).order('date',{ascending:true})
      .then(({ data }) => { setStockMovements(data||[]); setStockLoading(false); });
  }, [stockMonth]);

  const weekDates    = getWeekDates(selectedWeek.start);
  const venuesToShow = venueFilter !== 'All' ? [venueFilter] : venuesForTab;
  const deptToShow   = deptFilter === 'All' ? DEPTS : [deptFilter];
  const prepProducts = Array.from(new Set(stockMovements.map(m=>m.product_name).filter(Boolean))).sort();
  const productsForGraph = stockProduct==='All' ? prepProducts.slice(0,7) : [stockProduct];

  function visibleDepts(venue) { return deptToShow.filter(d=>!(NO_BAR_VENUES.includes(venue)&&d==='bar')); }
  function getEntry(venue,dept,date) { return stocktakeLogs.find(e=>e.venue===venue&&e.dept===dept&&e.date===date); }
  function completionRate(venue,dept) {
    let total=0,done=0;
    weekDates.forEach(date => {
      if (venue==='Prep Kitchen'&&['Sat','Sun'].includes(getDayName(date))) return;
      total++;
      if (getEntry(venue,dept,date)?.status==='submitted') done++;
    });
    return total===0?null:Math.round(done/total*100);
  }

  const stockLineData = (() => {
    if (!stockMovements.length) return [];
    const days = new Date(stockMonth.year, stockMonth.month+1, 0).getDate();
    return Array.from({ length:days }, (_,i) => {
      const d    = i+1;
      const date = `${stockMonth.year}-${String(stockMonth.month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      const row  = { date: String(d) };
      (stockProduct==='All'?prepProducts:[stockProduct]).forEach(p => {
        const match = stockMovements.find(m=>m.product_name===p&&m.date===date);
        row[p] = match ? match.qty : null;
      });
      return row;
    });
  })();

  return (
    <>
      <Card>
        <SectionHeader title="Stocktake Completion">
          <Select value={venueFilter} onChange={setVenueFilter} options={[{value:'All',label:'All Venues'},...venuesForTab.map(v=>({value:v,label:v}))]} />
          <Select value={deptFilter} onChange={setDeptFilter} options={[{value:'All',label:'All Depts'},...DEPTS.map(d=>({value:d,label:d.charAt(0).toUpperCase()+d.slice(1)}))]} />
          <Select value={WEEK_OPTIONS.findIndex(w=>w.label===selectedWeek.label)} onChange={v=>setSelectedWeek(WEEK_OPTIONS[+v])} options={WEEK_OPTIONS.map((w,i)=>({value:i,label:w.label}))} />
        </SectionHeader>
        <div style={{ display:'flex', gap:'0.65rem', flexWrap:'wrap', marginBottom:'1.25rem' }}>
          {venuesToShow.flatMap(venue => visibleDepts(venue).map(dept => {
            const rate = completionRate(venue,dept);
            return (
              <div key={`${venue}-${dept}`} style={{ flex:'1 1 140px', padding:'0.8rem 1rem', background:C.cream, borderRadius:'10px', border:`1px solid ${C.border}` }}>
                <p style={{ margin:'0 0 0.15rem', fontSize:'0.73rem', color:C.mid }}>{venue}</p>
                <p style={{ margin:'0 0 0.2rem', fontSize:'0.68rem', color:C.border, textTransform:'uppercase', fontWeight:600 }}>{dept}</p>
                <p style={{ margin:0, fontSize:'1.4rem', fontWeight:800, color:rateColor(rate) }}>{rate===null?'—':`${rate}%`}</p>
                <p style={{ margin:0, fontSize:'0.68rem', color:C.mid }}>this week</p>
              </div>
            );
          }))}
        </div>
        {loading ? <p style={{color:C.mid,fontSize:'0.85rem'}}>Loading…</p> : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'580px' }}>
              <thead><tr><th style={{...thStyle,minWidth:'120px'}}>Venue</th><th style={{...thStyle,minWidth:'65px'}}>Dept</th><WeekTh dates={weekDates} /></tr></thead>
              <tbody>
                {venuesToShow.flatMap(venue => {
                  const depts = visibleDepts(venue);
                  return depts.map((dept,di) => (
                    <tr key={`${venue}-${dept}`}>
                      {di===0 && <td style={{...tdStyle,fontWeight:600,verticalAlign:'middle'}} rowSpan={depts.length}>{venue}</td>}
                      <td style={{...tdStyle,color:C.mid,textTransform:'capitalize',fontSize:'0.78rem'}}>{dept}</td>
                      {weekDates.map(date => {
                        const day = getDayName(date);
                        if (venue==='Prep Kitchen'&&['Sat','Sun'].includes(day)) return <td key={date} style={{...tdStyle,background:C.cream,textAlign:'center'}} />;
                        const entry = getEntry(venue,dept,date);
                        const past  = isPast(date);
                        if (!entry) return <td key={date} style={{...tdStyle,textAlign:'center'}}>{past?<span style={{color:C.red}}>●</span>:<span style={{color:C.border}}>—</span>}</td>;
                        const ok = entry.status==='submitted';
                        return (
                          <td key={date} style={{...tdStyle,textAlign:'center'}}>
                            <span style={{fontWeight:700,color:ok?C.green:C.amber}} title={entry.submitted_by||''}>{ok?'✓':'⚠'}</span>
                            {entry.submitted_by && <div style={{fontSize:'0.62rem',color:C.mid}}>{entry.submitted_by}</div>}
                          </td>
                        );
                      })}
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>
        )}
        <TableLegend />
      </Card>
      <Card>
        <SectionHeader title="Stock Levels — Prep Kitchen">
          <Select value={stockProduct} onChange={setStockProduct} options={[{value:'All',label:'All Products (up to 7)'},...prepProducts.map(p=>({value:p,label:p}))]} />
          <Select value={`${stockMonth.year}-${stockMonth.month}`} onChange={v=>{ const [y,m]=v.split('-'); setStockMonth(MONTH_OPTIONS.find(o=>o.year===+y&&o.month===+m)); }} options={MONTH_OPTIONS.map(o=>({value:`${o.year}-${o.month}`,label:o.label}))} />
        </SectionHeader>
        {stockLoading ? <p style={{color:C.mid,fontSize:'0.85rem'}}>Loading…</p>
          : !stockMovements.length ? <p style={{color:C.mid,fontSize:'0.85rem'}}>No stock movements recorded for this month.</p>
          : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={stockLineData} margin={{top:4,right:8,left:0,bottom:4}}>
                <XAxis dataKey="date" tick={{fontSize:11}} />
                <YAxis tick={{fontSize:11}} />
                <Tooltip />
                <Legend />
                {productsForGraph.map((p,i) => <Line key={p} type="monotone" dataKey={p} stroke={LINE_COLORS[i%LINE_COLORS.length]} dot={{r:3}} connectNulls={false} strokeWidth={2} />)}
              </LineChart>
            </ResponsiveContainer>
          )}
      </Card>
    </>
  );
}

// ─── CHECKLISTS TAB ───────────────────────────────────────────────────────────
const CHECKLIST_VENUES = ['Box Jordaan', 'Box Plantage'];
const VENUE_IDENTITY   = {
  'Box Jordaan':  { bg:'#EDF3FC', text:'#2563EB', border:'#BAD4F5', short:'Jordaan'  },
  'Box Plantage': { bg:'#C8E6D8', text:'#2D7A65', border:'#7EA594', short:'Plantage' },
};
// Mid-shift venues — add venue name here when mid-shift is enabled for Plantage
const HAS_MIDSHIFT = ['Box Jordaan'];

function ChecklistsTab({ allowedVenues }) {
  const [selectedWeek,  setSelectedWeek]  = useState(WEEK_OPTIONS[0]);
  const [loading,       setLoading]       = useState(false);
  const [checklistLogs, setChecklistLogs] = useState([]);
  const [openVenue,     setOpenVenue]     = useState(null);  // venue name or null
  const [auditDrawer,   setAuditDrawer]   = useState(null);

  const venuesForTab = CHECKLIST_VENUES.filter(v => !allowedVenues || allowedVenues.includes(v));

  useEffect(() => {
    setLoading(true);
    const start = selectedWeek.start.toISOString().split('T')[0];
    const end   = selectedWeek.end.toISOString().split('T')[0];
    supabase.from('checklist_log').select('*').gte('date',start).lte('date',end)
      .then(({ data }) => { setChecklistLogs(data||[]); setLoading(false); });
  }, [selectedWeek]);

  const weekDates = getWeekDates(selectedWeek.start);

  // Shifts available per venue
  function shiftsForVenue(venue) {
    const base = ['opening'];
    if (HAS_MIDSHIFT.includes(venue)) base.push('mid-shift');
    base.push('closing');
    return base;
  }

  const getEntries = useCallback((venue, date, navShift) => {
    const subMap = SUB_SHIFT_MAP[navShift];
    if (subMap) {
      return {
        bar:   checklistLogs.find(e=>e.venue===venue&&e.date===date&&e.shift===subMap.bar)   || null,
        floor: checklistLogs.find(e=>e.venue===venue&&e.date===date&&e.shift===subMap.floor) || null,
      };
    }
    return { single: checklistLogs.find(e=>e.venue===venue&&e.date===date&&e.shift===navShift) || null };
  }, [checklistLogs]);

  // % complete for a venue+shift across the week (past days only)
  function shiftWeekRate(venue, navShift) {
    let total = 0, done = 0;
    weekDates.forEach(date => {
      if (!isPast(date)) return;
      const subMap = SUB_SHIFT_MAP[navShift];
      if (subMap) {
        total += 2;
        const { bar, floor } = getEntries(venue, date, navShift);
        if (bar?.status   === 'complete') done++;
        if (floor?.status === 'complete') done++;
      } else {
        total++;
        const { single } = getEntries(venue, date, navShift);
        if (single?.status === 'complete') done++;
      }
    });
    return total === 0 ? null : { done, total, pct: Math.round(done/total*100) };
  }

  // Overall venue completion rate
  function venueWeekRate(venue) {
    const shifts = shiftsForVenue(venue);
    let total = 0, done = 0;
    shifts.forEach(s => {
      const r = shiftWeekRate(venue, s);
      if (r) { total += r.total; done += r.done; }
    });
    return total === 0 ? null : Math.round(done/total*100);
  }

  function openAudit(venue, date, navShift) {
    const subMap = SUB_SHIFT_MAP[navShift];
    if (subMap) {
      const { bar, floor } = getEntries(venue, date, navShift);
      setAuditDrawer({ venue, date, shift: navShift, isSubShift: true, barEntry: bar, floorEntry: floor });
    } else {
      const { single } = getEntries(venue, date, navShift);
      setAuditDrawer({ venue, date, shift: navShift, entry: single || null });
    }
  }

  const closeAudit = useCallback(() => setAuditDrawer(null), []);
  const shiftLabel = s => s==='mid-shift'?'Mid':s.charAt(0).toUpperCase()+s.slice(1);

  // Cell colour for the detail grid
  function cellColor(status, isFuture) {
    if (isFuture) return { bg:'transparent', color:C.border, symbol:'—' };
    if (!status)  return { bg:C.redLight,    color:C.red,    symbol:'●' };
    if (status==='complete') return { bg:C.greenLight, color:C.green, symbol:'✓' };
    return { bg:C.amberLight, color:C.amber, symbol:'⚠' };
  }

  function subShiftCellStatus(bar, floor, isFuture) {
    if (isFuture) return { bg:'transparent', color:C.border, symbol:'—' };
    if (!bar && !floor) return { bg:C.redLight, color:C.red, symbol:'●' };
    const allDone = bar?.status==='complete' && floor?.status==='complete';
    if (allDone) return { bg:C.greenLight, color:C.green, symbol:'✓' };
    return { bg:C.amberLight, color:C.amber, symbol:'⚠' };
  }

  return (
    <>
      {/* Week selector */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.25rem', flexWrap:'wrap', gap:'0.65rem' }}>
        <h3 style={{ margin:0, fontSize:'1rem', fontWeight:700, color:C.charcoal }}>Checklist Completion</h3>
        <Select
          value={WEEK_OPTIONS.findIndex(w=>w.label===selectedWeek.label)}
          onChange={v=>setSelectedWeek(WEEK_OPTIONS[+v])}
          options={WEEK_OPTIONS.map((w,i)=>({value:i,label:w.label}))}
        />
      </div>

      {loading ? <p style={{color:C.mid,fontSize:'0.85rem'}}>Loading…</p> : (
        <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
          {venuesForTab.map(venue => {
            const identity    = VENUE_IDENTITY[venue];
            const overallRate = venueWeekRate(venue);
            const shifts      = shiftsForVenue(venue);
            const isOpen      = openVenue === venue;

            return (
              <div key={venue} style={{ background:C.white, borderRadius:'14px', border:`1px solid ${isOpen ? identity.border : C.border}`, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', transition:'border-color 0.15s' }}>

                {/* Venue header — always visible */}
                <div
                  onClick={() => setOpenVenue(isOpen ? null : venue)}
                  style={{ display:'flex', alignItems:'center', padding:'1rem 1.25rem', cursor:'pointer', gap:'1rem', background: isOpen ? identity.bg : C.white, transition:'background 0.15s' }}
                >
                  {/* Venue name */}
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', marginBottom:'0.65rem' }}>
                      <span style={{ fontSize:'1rem', fontWeight:800, color: identity.text }}>{identity.short}</span>
                      {overallRate !== null && (
                        <span style={{ fontSize:'0.78rem', fontWeight:700, color: rateColor(overallRate) }}>{overallRate}%</span>
                      )}
                      {overallRate === null && <span style={{ fontSize:'0.78rem', color:C.mid }}>No data</span>}
                    </div>

                    {/* Shift progress bars */}
                    <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap' }}>
                      {shifts.map(navShift => {
                        const r = shiftWeekRate(venue, navShift);
                        const pct = r ? r.pct : null;
                        return (
                          <div key={navShift} style={{ flex:'1 1 80px', minWidth:'70px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'3px' }}>
                              <span style={{ fontSize:'0.65rem', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.04em' }}>{shiftLabel(navShift)}</span>
                              <span style={{ fontSize:'0.65rem', color: pct===null ? C.border : rateColor(pct), fontWeight:600 }}>
                                {pct===null ? '—' : `${r.done}/${r.total}`}
                              </span>
                            </div>
                            <div style={{ height:'5px', background:`${identity.text}22`, borderRadius:'99px', overflow:'hidden' }}>
                              <div style={{ height:'100%', width:`${pct??0}%`, background: pct===null ? 'transparent' : rateColor(pct), borderRadius:'99px', transition:'width 0.4s ease' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <ChevronDown size={16} color={C.mid} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition:'transform 0.2s', flexShrink:0 }} />
                </div>

                {/* Detail grid — week × shift, expandable */}
                {isOpen && (
                  <div style={{ borderTop:`1px solid ${C.border}`, overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'480px' }}>
                      <thead>
                        <tr style={{ background:C.cream }}>
                          <th style={{ ...thStyle, minWidth:'70px' }}>Shift</th>
                          <WeekTh dates={weekDates} />
                        </tr>
                      </thead>
                      <tbody>
                        {shifts.map(navShift => {
                          const subMap = SUB_SHIFT_MAP[navShift];
                          return (
                            <tr key={navShift}>
                              <td style={{ ...tdStyle, fontWeight:600, fontSize:'0.78rem', color:C.mid, whiteSpace:'nowrap' }}>
                                {shiftLabel(navShift)}
                                {subMap && <div style={{fontSize:'0.62rem',color:C.border,fontWeight:400}}>B + F</div>}
                              </td>
                              {weekDates.map(date => {
                                const isFuture = !isPast(date);
                                let cell, isClickable = false;

                                if (subMap) {
                                  const { bar, floor } = getEntries(venue, date, navShift);
                                  const s = subShiftCellStatus(bar, floor, isFuture);
                                  isClickable = !isFuture;
                                  cell = (
                                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'2px' }}>
                                      <span style={{ fontWeight:700, fontSize:'0.85rem', color:s.color }}>{s.symbol}</span>
                                      {!isFuture && (
                                        <div style={{ display:'flex', gap:'2px', fontSize:'0.55rem' }}>
                                          <span style={{ color: bar?.status==='complete' ? C.green : bar ? C.amber : C.red }}>B</span>
                                          <span style={{ color: floor?.status==='complete' ? C.green : floor ? C.amber : C.red }}>F</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                } else {
                                  const { single: entry } = getEntries(venue, date, navShift);
                                  const status = isFuture ? null : (entry?.status || null);
                                  const s = cellColor(status, isFuture);
                                  isClickable = !isFuture;
                                  const done  = (entry?.items||[]).filter(i=>i.initials).length;
                                  const total = (entry?.items||[]).length;
                                  cell = (
                                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'1px' }}>
                                      <span style={{ fontWeight:700, fontSize:'0.85rem', color:s.color }}>{s.symbol}</span>
                                      {entry && total > 0 && <span style={{ fontSize:'0.55rem', color:C.mid }}>{done}/{total}</span>}
                                    </div>
                                  );
                                }

                                return (
                                  <td
                                    key={date}
                                    onClick={isClickable ? () => openAudit(venue, date, navShift) : undefined}
                                    style={{ ...tdStyle, textAlign:'center', padding:'0.5rem 0.25rem', cursor:isClickable?'pointer':'default', transition:'background 0.1s' }}
                                    onMouseEnter={e=>{ if(isClickable) e.currentTarget.style.background=C.cream; }}
                                    onMouseLeave={e=>{ e.currentTarget.style.background=''; }}
                                  >
                                    {cell}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{ padding:'0.6rem 1rem', fontSize:'0.72rem', color:C.mid }}>
                      Tap any past cell to view full audit detail
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {auditDrawer && (
        auditDrawer.isSubShift ? (
          <MidShiftAuditDrawer
            venue={auditDrawer.venue}
            date={auditDrawer.date}
            shiftLabel={auditDrawer.shift === 'closing' ? 'Closing' : 'Mid-Shift'}
            barEntry={auditDrawer.barEntry}
            floorEntry={auditDrawer.floorEntry}
            onClose={closeAudit}
          />
        ) : (
          <ChecklistAuditDrawer
            entry={auditDrawer.entry}
            venue={auditDrawer.venue}
            shift={auditDrawer.shift}
            date={auditDrawer.date}
            onClose={closeAudit}
          />
        )
      )}
    </>
  );
}

// ─── SHIFTY TAB ───────────────────────────────────────────────────────────────
const SHIFTY_VENUES  = ['Box Jordaan', 'Box Plantage'];
const SHIFTY_PROMPTS = [
  { key:'service_notes',     label:'Service'      },
  { key:'staff_notes',       label:'Staff'        },
  { key:'recognition_notes', label:'Recognition'  },
  { key:'upselling_notes',   label:'Upselling'    },
  { key:'support_notes',     label:'Team Support' },
  { key:'stock_notes',       label:'Stock'        },
  { key:'guest_notes',       label:'Guests'       },
];

const SHIFTY_DAY_NAMES   = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const SHIFTY_MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function shiftyGetFirstDay(year, month) { const d = new Date(year,month,1).getDay(); return d===0?6:d-1; }
function shiftyDaysInMonth(year, month) { return new Date(year,month+1,0).getDate(); }
function shiftyPad(n) { return String(n).padStart(2,'0'); }

const venueTheme = {
  'Box Jordaan':  { active:C.orange,  shadow:'rgba(228,172,73,0.25)',  bg:C.orangeLight  },
  'Box Plantage': { active:C.skyText, shadow:'rgba(58,143,163,0.25)',  bg:C.skyBlueLight },
};

// Single expanded report card
function ShiftyReportCard({ r, expanded, onToggle }) {
  const vTheme = venueTheme[r.venue];
  const filledCount = SHIFTY_PROMPTS.filter(({key}) => r[key]).length;
  return (
    <div style={{ background:C.white, borderRadius:'10px', border:`1px solid ${C.border}`, overflow:'hidden', marginBottom:'0.5rem', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
      <div
        onClick={onToggle}
        style={{ display:'flex', alignItems:'center', padding:'0.75rem 1rem', cursor:'pointer', gap:'0.65rem' }}
      >
        {/* Shift dot */}
        <div style={{ width:'8px', height:'8px', borderRadius:'50%', flexShrink:0, background: r.shift==='brunch' ? C.orange : C.navy }} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap' }}>
            <span style={{ fontWeight:700, fontSize:'0.85rem', color:C.charcoal, textTransform:'capitalize' }}>{r.shift}</span>
            {r.manager_name && <span style={{ fontSize:'0.78rem', color:C.mid }}>— {r.manager_name}</span>}
            {vTheme && (
              <span style={{ fontSize:'0.68rem', fontWeight:700, color:vTheme.active, background:vTheme.bg, padding:'0.1rem 0.45rem', borderRadius:'999px' }}>{r.venue}</span>
            )}
          </div>
          <div style={{ fontSize:'0.72rem', color:C.mid, marginTop:'0.08rem' }}>
            {r.submitted_at ? new Date(r.submitted_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}) : ''}
            <span style={{ marginLeft:'0.5rem', color: filledCount===SHIFTY_PROMPTS.length ? C.green : C.mid }}>
              {filledCount}/{SHIFTY_PROMPTS.length} fields
            </span>
          </div>
        </div>
        {expanded ? <ChevronUp size={13} style={{color:C.mid,flexShrink:0}}/> : <ChevronDown size={13} style={{color:C.mid,flexShrink:0}}/>}
      </div>
      {expanded && (
        <div style={{ borderTop:`1px solid ${C.border}`, padding:'0.85rem 1rem', display:'flex', flexDirection:'column', gap:'0.65rem' }}>
          {SHIFTY_PROMPTS.map(({key,label}) => r[key] ? (
            <div key={key}>
              <p style={{ margin:'0 0 0.18rem', fontSize:'0.68rem', fontWeight:700, color:C.pinkActive, textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</p>
              <p style={{ margin:0, fontSize:'0.85rem', color:C.charcoal, lineHeight:1.55 }}>{r[key]}</p>
            </div>
          ) : null)}
          {SHIFTY_PROMPTS.every(({key})=>!r[key]) && <p style={{color:C.mid,fontSize:'0.83rem'}}>No notes recorded.</p>}
        </div>
      )}
    </div>
  );
}

function ShiftyTab({ isManager, allowedVenues }) {
  const shiftyVenuesForTab = useMemo(
    () => allowedVenues ? SHIFTY_VENUES.filter(v => allowedVenues.includes(v)) : SHIFTY_VENUES,
    [allowedVenues]
  );

  const now = new Date();
  const [currentYear,  setCurrentYear]  = useState(now.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [selectedDay,  setSelectedDay]  = useState(now.getDate());
  const [reports,      setReports]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [expanded,     setExpanded]     = useState({});

  const todayStr = now.toISOString().split('T')[0];

  // Load the whole month at once
  useEffect(() => {
    setLoading(true);
    const start = `${currentYear}-${shiftyPad(currentMonth+1)}-01`;
    const end   = `${currentYear}-${shiftyPad(currentMonth+1)}-${shiftyPad(shiftyDaysInMonth(currentYear,currentMonth))}`;
    supabase.from('shift_log').select('*')
      .in('venue', shiftyVenuesForTab)
      .gte('date', start).lte('date', end)
      .order('submitted_at', {ascending:false})
      .limit(500)
      .then(({ data }) => { setReports(data||[]); setLoading(false); });
  }, [currentYear, currentMonth, shiftyVenuesForTab]);

  function toggle(id) { setExpanded(prev=>({...prev,[id]:!prev[id]})); }

  const prevMonth = () => {
    if (currentMonth===0) { setCurrentYear(y=>y-1); setCurrentMonth(11); }
    else setCurrentMonth(m=>m-1);
    setSelectedDay(1);
  };
  const nextMonth = () => {
    if (currentMonth===11) { setCurrentYear(y=>y+1); setCurrentMonth(0); }
    else setCurrentMonth(m=>m+1);
    setSelectedDay(1);
  };

  // Reports grouped by date
  const grouped = reports.reduce((acc,r) => {
    const key = r.date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  // Dates that have reports (for dot indicators)
  const datesWithReports = new Set(Object.keys(grouped));

  const daysInMon  = shiftyDaysInMonth(currentYear, currentMonth);
  const firstDay   = shiftyGetFirstDay(currentYear, currentMonth);
  const totalCells = Math.ceil((daysInMon + firstDay) / 7) * 7;

  // Selected day reports
  const selectedDateStr  = `${currentYear}-${shiftyPad(currentMonth+1)}-${shiftyPad(selectedDay)}`;
  const selectedDayReps  = grouped[selectedDateStr] || [];
  const isSelectedToday  = selectedDateStr === todayStr;


  return (
    <div style={{ maxWidth:'720px' }}>

      {/* Calendar grid */}
      <div style={{ background:C.white, borderRadius:'14px', border:`1px solid ${C.border}`, overflow:'hidden', marginBottom:'1.25rem', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>

        {/* Month nav */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.85rem 1rem', borderBottom:`1px solid ${C.border}` }}>
          <button onClick={prevMonth} style={{ background:'none', border:`1px solid ${C.border}`, borderRadius:'7px', padding:'0.3rem 0.55rem', cursor:'pointer', display:'flex', alignItems:'center', color:C.charcoal }}>
            <ChevronDown size={14} style={{ transform:'rotate(90deg)' }} />
          </button>
          <div style={{ textAlign:'center' }}>
            <span style={{ fontSize:'1rem', fontWeight:800, color:C.charcoal, letterSpacing:'-0.02em' }}>
              {SHIFTY_MONTH_NAMES[currentMonth]} <span style={{ color:C.mid, fontWeight:400 }}>{currentYear}</span>
            </span>
            {!loading && <span style={{ display:'block', fontSize:'0.7rem', color:C.mid, marginTop:'1px' }}>{Object.keys(grouped).length} day{Object.keys(grouped).length!==1?'s':''} with reports</span>}
          </div>
          <button onClick={nextMonth} style={{ background:'none', border:`1px solid ${C.border}`, borderRadius:'7px', padding:'0.3rem 0.55rem', cursor:'pointer', display:'flex', alignItems:'center', color:C.charcoal }}>
            <ChevronDown size={14} style={{ transform:'rotate(-90deg)' }} />
          </button>
        </div>

        {/* Day headers */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', background:C.skyBlueLight, borderBottom:`1px solid ${C.skyBlue}` }}>
          {SHIFTY_DAY_NAMES.map((d,i) => (
            <div key={d} style={{ padding:'0.55rem 0', textAlign:'center', fontSize:'0.65rem', fontWeight:700, color: i>=5 ? C.amber : C.skyText, letterSpacing:'0.06em', textTransform:'uppercase' }}>{d}</div>
          ))}
        </div>

        {/* Grid cells */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', padding:'4px 2px 8px' }}>
          {Array.from({length:totalCells}).map((_,idx) => {
            const dayNum = idx - firstDay + 1;
            const isCurrentMonth = dayNum >= 1 && dayNum <= daysInMon;
            const dateStr = isCurrentMonth ? `${currentYear}-${shiftyPad(currentMonth+1)}-${shiftyPad(dayNum)}` : null;
            const isToday    = dateStr === todayStr;
            const isSelected = isCurrentMonth && dayNum === selectedDay;
            const hasRep     = dateStr && datesWithReports.has(dateStr);
            const repCount   = hasRep ? (grouped[dateStr]||[]).length : 0;
            const isWeekend  = idx % 7 >= 5;

            return (
              <div
                key={idx}
                onClick={() => { if (isCurrentMonth) setSelectedDay(dayNum); }}
                style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'3px', padding:'3px 1px', cursor:isCurrentMonth?'pointer':'default' }}
              >
                <div style={{
                  width:'30px', height:'30px', borderRadius:'50%',
                  background: isSelected ? (isToday ? C.navy : C.pinkActive) : 'transparent',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  transition:'all 0.15s',
                }}>
                  <span style={{
                    fontSize:'12px', fontWeight: isSelected||isToday ? 800 : 400, lineHeight:1,
                    color: isSelected
                      ? C.white
                      : isCurrentMonth ? (isWeekend ? C.amber : C.charcoal) : '#C8D4DC',
                  }}>
                    {isCurrentMonth ? dayNum : ''}
                  </span>
                </div>
                {/* Report dot(s) */}
                <div style={{ height:'5px', display:'flex', alignItems:'center', justifyContent:'center', gap:'2px' }}>
                  {hasRep && Array.from({length:Math.min(repCount,3)}).map((_,i) => (
                    <div key={i} style={{ width:'4px', height:'4px', borderRadius:'50%', background: isSelected ? 'rgba(255,255,255,0.6)' : C.pinkActive }} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected day reports */}
      <div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.65rem' }}>
          <div>
            <span style={{ fontSize:'0.88rem', fontWeight:800, color:C.charcoal }}>
              {isSelectedToday ? 'Today' : `${SHIFTY_DAY_NAMES[new Date(selectedDateStr+'T00:00:00').getDay()===0?6:new Date(selectedDateStr+'T00:00:00').getDay()-1]}, ${selectedDay} ${SHIFTY_MONTH_NAMES[currentMonth].slice(0,3)}`}
            </span>
          </div>
          {!loading && (
            <span style={{ fontSize:'0.75rem', color:C.mid }}>
              {selectedDayReps.length} report{selectedDayReps.length!==1?'s':''}
            </span>
          )}
        </div>

        {loading ? (
          <p style={{color:C.mid,fontSize:'0.85rem'}}>Loading…</p>
        ) : selectedDayReps.length === 0 ? (
          <div style={{ padding:'1.5rem', textAlign:'center', color:C.mid, fontSize:'0.85rem', background:C.white, borderRadius:'10px', border:`1px solid ${C.border}` }}>
            No shift reports for this day
          </div>
        ) : (
          selectedDayReps.map(r => (
            <ShiftyReportCard key={r.id} r={r} expanded={!!expanded[r.id]} onToggle={()=>toggle(r.id)} />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Tab theme ────────────────────────────────────────────────────────────────
const TAB_THEME = {
  transfers:   { active:C.navy,       shadow:'rgba(68,83,102,0.22)'   },
  stocktake:   { active:C.skyText,    shadow:'rgba(58,143,163,0.22)'  },
  checklists:  { active:C.green,      shadow:'rgba(126,165,148,0.22)' },
  shifty:      { active:C.pinkActive, shadow:'rgba(196,96,122,0.22)'  },
  cashup:      { active:C.green,      shadow:'rgba(126,165,148,0.22)' },
  fridgetemps: { active:C.orange,     shadow:'rgba(228,172,73,0.22)'  },
  cleaning:    { active:'#8B7EC8',    shadow:'rgba(139,126,200,0.22)' },
};

// ─── Tab nav — mobile: horizontal scroll strip; desktop: pill row ─────────────
function TabNav({ visibleTabs, activeTab, onSelect }) {
  const isMobile = window.innerWidth < 640;

  if (isMobile) {
    return (
      <div style={{
        display: 'flex', overflowX: 'auto', gap: '0',
        marginBottom: '1.25rem', marginLeft: '-1.25rem', marginRight: '-1.25rem',
        paddingLeft: '1.25rem', paddingRight: '1.25rem',
        borderBottom: `1px solid ${C.border}`,
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch',
      }}>
        {visibleTabs.map(tab => {
          const Icon     = tab.icon;
          const isActive = activeTab === tab.key;
          const theme    = TAB_THEME[tab.key] || TAB_THEME.checklists;
          return (
            <button
              key={tab.key}
              onClick={() => onSelect(tab.key)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                padding: '0.55rem 0.85rem 0.6rem',
                background: 'none', border: 'none', cursor: 'pointer',
                flexShrink: 0,
                borderBottom: `2.5px solid ${isActive ? theme.active : 'transparent'}`,
                transition: 'all 0.15s',
              }}
            >
              <Icon size={18} strokeWidth={isActive ? 2.2 : 1.6} color={isActive ? theme.active : C.mid} />
              <span style={{ fontSize: '0.65rem', fontWeight: isActive ? 700 : 500, color: isActive ? theme.active : C.mid, whiteSpace: 'nowrap' }}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
      {visibleTabs.map(tab => {
        const Icon     = tab.icon;
        const isActive = activeTab === tab.key;
        const theme    = TAB_THEME[tab.key] || TAB_THEME.checklists;
        return (
          <button key={tab.key} onClick={() => onSelect(tab.key)} style={{ display:'flex', alignItems:'center', gap:'0.38rem', padding:'0.48rem 0.9rem', borderRadius:'10px', border:`2px solid ${isActive?theme.active:C.border}`, background:isActive?theme.active:C.white, color:isActive?C.white:C.mid, fontSize:'0.8rem', fontWeight:isActive?700:500, cursor:'pointer', transition:'all 0.15s', boxShadow:isActive?`0 2px 8px ${theme.shadow}`:'none' }}>
            <Icon size={14} strokeWidth={isActive?2.2:1.8} />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Cleaning Tab (stub — full history view coming soon) ──────────────────────
function CleaningTab({ allowedVenues, currentUser }) {
  return (
    <div>
      <SectionHeader title="Cleaning & Pest Check History">
        <span style={{ fontSize:'0.78rem', color:'#8B7EC8', fontWeight:600 }}>Coming soon</span>
      </SectionHeader>
      <Card>
        <div style={{ padding:'2.5rem 1.5rem', textAlign:'center' }}>
          <div style={{ fontSize:'2rem', marginBottom:'0.75rem' }}>🧹</div>
          <p style={{ margin:'0 0 0.5rem', fontSize:'0.95rem', fontWeight:700, color:'#2F3A42' }}>Cleaning History</p>
          <p style={{ margin:0, fontSize:'0.84rem', color:'#7A8A96', lineHeight:1.6, maxWidth:'340px', marginLeft:'auto', marginRight:'auto' }}>
            Full cleaning checklist history, completion rates, and pest check logs will appear here.
            Individual item completion timelines and venue comparisons are in development.
          </p>
        </div>
      </Card>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function Reports({ transferLog = [], checklistLog = [], currentUser }) {
  const isAdmin       = isAdminUser(currentUser);
  const isManager     = ['admin','manager','supervisor'].includes(currentUser?.role);
  const allowedVenues = isAdmin ? ALL_VENUES : getUserVenues(currentUser).filter(v=>ALL_VENUES.includes(v));

  const [activeTab, setActiveTab] = useState(isAdmin ? 'transfers' : 'checklists');  const visibleTabs = TABS.filter(t => !t.adminOnly || isAdmin);

  // Read intent set by Homepage hotlinks
  useEffect(() => {
    try {
      const intent = localStorage.getItem('reports_intent_tab');
      if (intent) {
        const valid = visibleTabs.find(t => t.key === intent);
        if (valid) setActiveTab(intent);
        localStorage.removeItem('reports_intent_tab');
      }
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isManager) {
    return (
      <div style={{ padding:'1.25rem', maxWidth:'1060px', margin:'0 auto', background:C.cream, minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <p style={{ color:C.mid, fontSize:'0.9rem' }}>Nothing to see here.</p>
      </div>
    );
  }

  return (
    <div style={{ padding:'1.25rem', maxWidth:'1060px', margin:'0 auto', background:C.cream, minHeight:'100vh' }}>
      <div style={{ marginBottom:'1rem' }}>
        <h2 style={{ margin:'0 0 0.18rem', fontSize:'1.2rem', fontWeight:700, color:C.charcoal }}>Reports</h2>
        <p style={{ margin:0, fontSize:'0.8rem', color:C.mid }}>{new Date().toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</p>
      </div>

      <TabNav visibleTabs={visibleTabs} activeTab={activeTab} onSelect={setActiveTab} />

      {activeTab==='transfers'   && <TransfersTab transferLog={transferLog} />}
      {activeTab==='stocktake'   && <StocktakeTab allowedVenues={allowedVenues} />}
      {activeTab==='checklists'  && <ChecklistsTab allowedVenues={allowedVenues} />}
      {activeTab==='cleaning'    && <CleaningTab allowedVenues={allowedVenues} currentUser={currentUser} />}
      {activeTab==='shifty'      && <ShiftyTab isManager={isManager} allowedVenues={allowedVenues} />}
      {activeTab==='fridgetemps' && <FridgeTempsTab currentUser={currentUser} isAdmin={isAdmin} />}
    </div>
  );
}